import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Box, useTheme } from '@mui/material'
import { observer } from "mobx-react"

import { Icons, constrainPan, findHoveredShape, getPos, isPointInsideElement, VIRTUALBOARD_TOOLS, normalizePoint, getCanvasCSSSize } from '../../utils/virtualboard-utilities'
import { useVirtualboardStore } from "../../store/virtualboard.store"
import { debounce } from "../../utils/utilities"
import CanvasBottomNavbar from './CanvasBottomNavbar'
import VirtualboardTools from './VirtualboardTools'
import MenuBar from './MenuBar'


const getCustomCursorOffset = ( tool ) => {
    let x = 0, y = 0
    switch ( tool ) {
        case "rect":
        case "circle":
            x = -6
            y = -10
            break
        case "pen":
        case "pencil":
        case "marker":
            y = -15
            break
        case "select":
            y = -4
            x = -3
            break
        case "text":
            x = -6
            y = -10
            break
        case "eraser":
            x = -6
            y = -10
            break
        default:
            break

    }
    return { x, y }
}

function wrapText ( ctx, text = "", x, y, maxWidth, lineHeight ) {
    const paragraphs = text.split( "\n" ) // Split on manual line breaks

    for ( let p = 0; p < paragraphs.length; p++ ) {
        const words = paragraphs[p].split( " " )
        let line = ""

        for ( let n = 0; n < words.length; n++ ) {
            const testLine = line + words[n] + " "
            const testWidth = ctx.measureText( testLine ).width

            if ( testWidth > maxWidth && n > 0 ) {
                ctx.fillText( line, x, y )
                line = words[n] + " "
                y += lineHeight
            } else {
                line = testLine
            }
        }

        // Draw the last line of the paragraph
        ctx.fillText( line, x, y )
        y += lineHeight // move down after each paragraph
    }
}



const Virtualboard = observer( ( { document, onChange = () => { }, onMouseMove = () => { }, elements = {} } ) => {

    const [newTextElementAdder, setNewTextElementAdder] = useState( null )
    const [currentImage, setCurrentImage] = useState( null )
    const [textEditing, setTextEditing] = useState( null )
    const [hoveredId, setHoveredId] = useState( null )

    const store = useVirtualboardStore()

    const { shadows, palette } = useTheme()

    const canvasWrapperRef = useRef( null )
    const canvasCursorRef = useRef( null )
    const drawCanvasRef = useRef( null )
    const tempCanvasRef = useRef( null )
    const containerRef = useRef( null )
    const bgCanvasRef = useRef( null )
    const drawCanvasCtxRef = useRef( null )
    const bgCanvasCtxRef = useRef( null )
    const tempCanvasCtxRef = useRef( null )
    const textEditorRef = useRef( null )

    const strokeTime = useRef( new Date().getTime() )
    const panStartRef = useRef( { x: 0, t: 0 } )
    const strictModeUpdatedRef = useRef( false )
    const currentElementRef = useRef( null )
    const strictModeRef = useRef( false )
    const drawingRef = useRef( false )
    const panningRef = useRef( false )
    const smoothStrokeWidthRef = useRef( null )
    const bgImgRef = useRef()
    const movingElementsRef = useRef( false )
    const movingOffsetRef = useRef( { x: 0, y: 0 } )
    const movingStartRef = useRef( { x: 0, y: 0 } )
    const movedAfterElements = useRef( [] )
    const selectedForDeleting = useRef( new Set() )

    const toggleFullScreen = () => {
        if ( !window.document.fullscreenElement ) {
            if ( containerRef.current ) {
                if ( containerRef.current.requestFullscreen ) {
                    containerRef.current.requestFullscreen()
                } else if ( containerRef.current.webkitRequestFullscreen ) {
                    // Safari
                    containerRef.current.webkitRequestFullscreen()
                } else if ( containerRef.current.msRequestFullscreen ) {
                    // IE/Edge
                    containerRef.current.msRequestFullscreen()
                }
            }
            store.setContainerState( { fullscreen: true } )
        } else {
            window.document.exitFullscreen()
            store.setContainerState( { fullscreen: false } )
        }
    }

    const downloadMergedCanvas = () => {
        const bgCanvas = bgCanvasRef.current
        const drawCanvas = drawCanvasRef.current
        if ( !bgCanvas || !drawCanvas ) return

        // 1️⃣ Create a new offscreen canvas
        const mergedCanvas = window.document.createElement( 'canvas' )
        const width = bgCanvas.width
        const height = bgCanvas.height
        mergedCanvas.width = width
        mergedCanvas.height = height
        const ctx = mergedCanvas.getContext( '2d' )

        // 2️⃣ Draw canvases in order (bottom → top)
        ctx.drawImage( bgCanvas, 0, 0, width, height )
        ctx.drawImage( drawCanvas, 0, 0, width, height )

        // 3️⃣ Convert to downloadable image
        mergedCanvas.toBlob( ( blob ) => {
            const link = window.document.createElement( 'a' )
            link.href = URL.createObjectURL( blob )
            link.download = 'virtualboard.png'
            link.click()
            URL.revokeObjectURL( link.href )
        }, 'image/png' )
    }

    const addNewEventsToList = useCallback( ( event ) => {
        const events = store.addEvent( event )

        if ( onChange && typeof onChange === 'function' && events )
            onChange( event, { elements: store.getElements, pageId: store.getBoardState?.document?.slides[store.getBoardState?.currentSlide].id } )
    }, [onChange, store] )

    const handleTextChangeOnNewTextEleEditor = ( e, setValue ) => {
        const el = e.target
        let height, width
        el.style.height = el.scrollHeight + 'px'
        height = el.scrollHeight

        // For width — rough estimation
        const temp = window.document.createElement( 'span' )
        temp.style.visibility = 'hidden'
        temp.style.whiteSpace = 'pre'
        temp.style.font = getComputedStyle( el ).font
        temp.textContent = el.value.replace( /\n/g, '\n_' )
        window.document.body.appendChild( temp )
        el.style.width = temp.offsetWidth + 20 + 'px'
        width = temp.offsetWidth + 20
        window.document.body.removeChild( temp )
        setValue( prev => ( { ...prev, text: e.target.value, width: normalizeWidth( width ), height: normalizeHeight( height ) } ) )
    }

    // Convert normalized point to actual pixel coordinates
    const denormalizePoint = ( p ) => {
        const { width, height } = getCanvasCSSSize( drawCanvasRef.current )
        return { x: p.x * width, y: p.y * height }
    }

    const normalizeWidth = ( length ) => {
        const { width } = getCanvasCSSSize( drawCanvasRef.current )
        return length / width
    }

    const normalizeHeight = ( length ) => {
        const { height } = getCanvasCSSSize( drawCanvasRef.current )
        return length / height
    }

    const denormalizeWidth = ( length ) => {
        const { width } = getCanvasCSSSize( drawCanvasRef.current )
        return length * width
    }

    const denormalizeHeight = ( length ) => {
        const { height } = getCanvasCSSSize( drawCanvasRef.current )
        return length * height
    }

    const setSelectedElements = useCallback( ( value ) => {
        store.setBoardState( { selected: value } )
    }, [store] )

    const drawStroke = useCallback( ( ctx, element ) => {
        const { lineWidth, lineDash, lineJoin, composite, color } = element
        const points = element.points
        if ( points.length < 2 ) return

        const p1 = denormalizePoint( points.at( -2 ) )
        const p2 = denormalizePoint( points.at( -1 ) )

        // --- Time-based smoothing ---
        const now = performance.now()
        const dt = Math.max( now - ( strokeTime.current || now ), 1 )
        strokeTime.current = now

        const dx = p2.x - p1.x
        const dy = p2.y - p1.y
        const dist = Math.sqrt( dx * dx + dy * dy )
        const speed = dist / dt

        // --- Smooth width transition ---
        const maxWidth = lineWidth * 1.1
        const minWidth = lineWidth * 0.3
        const factor = 50 // sensitivity

        const targetWidth = Math.max( minWidth, Math.min( maxWidth, maxWidth - speed * factor ) )

        // Exponential smoothing to avoid jitter
        const lastWidth = smoothStrokeWidthRef.current ?? lineWidth
        const smoothWidth = lastWidth + ( targetWidth - lastWidth ) * 0.2
        smoothStrokeWidthRef.current = smoothWidth

        // --- Midpoint for curve ---
        const midX = ( p1.x + p2.x ) / 2
        const midY = ( p1.y + p2.y ) / 2

        ctx.save()
        ctx.lineCap = "round"
        ctx.lineJoin = lineJoin
        ctx.strokeStyle = color
        ctx.lineWidth = smoothWidth
        if ( composite ) ctx.globalCompositeOperation = composite
        ctx.setLineDash( lineDash || [] )

        // --- Draw continuous quadratic curve ---
        ctx.beginPath()
        ctx.moveTo( p1.x, p1.y )
        ctx.quadraticCurveTo( midX, midY, p2.x, p2.y )
        ctx.stroke()
        ctx.restore()
    }, [] )

    const drawLine = useCallback( ( ctx, element ) => {
        const { lineWidth, lineDash, lineJoin, composite, color } = element
        const p1 = denormalizePoint( element.points.at( 0 ), drawCanvasRef.current.width, drawCanvasRef.current.height )
        const p2 = denormalizePoint( element.points.at( -1 ), drawCanvasRef.current.width, drawCanvasRef.current.height )

        ctx.save()
        ctx.strokeStyle = "solid"
        ctx.lineWidth = lineWidth
        ctx.lineCap = "round"
        ctx.lineDash = lineDash
        ctx.strokeStyle = color
        ctx.lineJoin = lineJoin
        if ( composite ) ctx.globalCompositeOperation = composite
        ctx.beginPath()
        ctx.moveTo( p1.x, p1.y )
        ctx.lineTo( p2.x, p2.y )
        ctx.stroke()
        ctx.restore()
    }, [] )


    const drawCurve = useCallback( ( ctx, element ) => {
        let { lineJoin, points, lineWidth = 1, composite, lineDash, colorInfo, id, opacity } = element
        points = element.points.map( p => denormalizePoint( p ) )

        ctx.save()
        ctx.lineWidth = lineWidth
        ctx.lineCap = "round"
        ctx.lineJoin = lineJoin
        if ( composite ) ctx.globalCompositeOperation = composite
        ctx.beginPath()
        ctx.setLineDash( lineDash )
        ctx.moveTo( points[0].x, points[0].y )
        for ( let i = 1; i < points.length - 1; i++ ) {
            const xc = ( points[i].x + points[i + 1].x ) / 2
            const yc = ( points[i].y + points[i + 1].y ) / 2
            ctx.quadraticCurveTo( points[i].x, points[i].y, xc, yc )
        }
        const lastPoint = points[points.length - 1]
        ctx.lineTo( lastPoint.x, lastPoint.y )
        ctx.stroke()
        ctx.restore()
    }, [] )

    const drawElement = useCallback( ( ctx, element ) => {

        const { type, fill, id, radiusX, radiusY, fontSize, text, fontFamily = "Roboto", centerX, centerY, x: startX, y: startY, width, height, opacity, composite, lineJoin, lineCap, lineWidth, colorInfo, lineDash } = element

        ctx.save()
        ctx.lineWidth = lineWidth
        let color = "#000000"
        if ( selectedForDeleting.current.has( id ) ) {
            const rgbcolor = colorInfo
            color = `rgba(${rgbcolor.r},${rgbcolor.g},${rgbcolor.b},0.2)`
        } else {
            const rgbcolor = colorInfo
            color = `rgba(${rgbcolor.r},${rgbcolor.g},${rgbcolor.b},${opacity})`
        }
        ctx.lineCap = lineCap ?? "round"
        ctx.lineJoin = lineJoin
        ctx.strokeStyle = color
        if ( fill )
            ctx.fillStyle = color
        if ( composite )
            ctx.globalCompositeOperation = composite
        ctx.beginPath()

        ctx.setLineDash( lineDash )
        const board = getCanvasCSSSize( drawCanvasRef.current )

        if ( type === "stroke" ) {
            drawCurve( ctx, element )
        } else if ( type === "rect" ) {
            const x = startX * board.width
            const y = startY * board.height
            const w = width * board.width
            const h = height * board.height
            if ( fill )
                ctx.fillRect( x, y, w, h )
            else
                ctx.strokeRect( x, y, w, h )
        } else if ( type === "circle" ) {
            const cx = centerX * board.width
            const cy = centerY * board.height
            const rx = radiusX * board.width
            const ry = radiusY * board.height
            ctx.ellipse( cx, cy, rx, ry, 0, 0, 2 * Math.PI )
        } else if ( type === "text" ) {
            ctx.font = `${denormalizeHeight( fontSize )}px ${fontFamily}`
            ctx.fillStyle = color

            const posX = startX * board.width
            const posY = startY * board.height
            const denormalizedFontSize = denormalizeHeight( fontSize )

            const textBoxWidth = denormalizeWidth( width )

            wrapText( ctx, text, posX, posY + denormalizedFontSize, textBoxWidth, denormalizedFontSize * 1.2 )
        }

        if ( fill )
            ctx.fill()
        else
            ctx.stroke()
        ctx.restore()
    }, [drawCurve] )

    const previewTextElement = ( ctx, el ) => {
        const dpr = window.devicePixelRatio || 1
        ctx.setTransform( 1, 0, 0, 1, 0, 0 )
        ctx.clearRect( 0, 0, ctx.canvas.width, ctx.canvas.height )
        ctx.save()
        ctx.lineWidth = el.lineWidth

        ctx.strokeStyle = el.color
        ctx.lineCap = el.lineCap ?? "round"
        ctx.lineJoin = el.lineJoin
        ctx.opacity = el.opacity
        if ( el.composite ) ctx.globalCompositeOperation = el.composite
        ctx.beginPath()

        ctx.setLineDash( el.lineDash )
        const board = getCanvasCSSSize( drawCanvasRef.current )

        if ( el.type === "text" ) {
            ctx.font = `${el.fontSize}px ${el.fontFamily}`
            ctx.fillStyle = el.color
            const x = el.x * board.width
            const y = el.y * board.height
            const width = el.width * board.width
            const height = el.height * board.height
            ctx.strokeRect( x, y, width, height )

            // wrapText( ctx, el.text, x, y + el.fontSize, el.width, el.fontSize * 1.2 )
        }
        ctx.stroke()
        ctx.restore()
    }

    const drawHighlighter = useCallback( ( ctx, el ) => {
        ctx.save()
        ctx.strokeStyle = "#0e7ccd"
        ctx.lineWidth = 1
        ctx.shadowBlur = 10
        ctx.shadowColor = "#0e7ccd"
        ctx.beginPath()

        ctx.setLineDash( el.lineDash )
        const board = getCanvasCSSSize( drawCanvasRef.current )

        if ( el.type === "stroke" ) {
            const firstPoint = denormalizePoint( el.points[0] )
            ctx.moveTo( firstPoint.x, firstPoint.y )
            for ( let i = 1; i < el.points.length; i++ ) {
                const denormalizedPoint = denormalizePoint( el.points[i] )
                ctx.lineTo( denormalizedPoint.x, denormalizedPoint.y )
            }
        } else if ( el.type === "rect" || el.type === "text" ) {
            const x = el.x * board.width
            const y = el.y * board.height
            const width = el.width * board.width
            const height = el.height * board.height
            ctx.strokeRect( x, y, width, height )
        } else if ( el.type === "circle" ) {
            const centerX = el.centerX * board.width
            const centerY = el.centerY * board.height
            const radiusX = el.radiusX * board.width
            const radiusY = el.radiusY * board.height
            ctx.ellipse( centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI )
        }

        ctx.stroke()
        ctx.restore()
    }, [] )

    const drawSelected = useCallback( ( selectedElements, elements ) => {
        const ctx = tempCanvasCtxRef.current
        const dpr = window.devicePixelRatio || 1
        ctx.setTransform( 1, 0, 0, 1, 0, 0 )
        ctx.clearRect( 0, 0, ctx.canvas.width, ctx.canvas.height )
        const zoom = store.getZoom
        const pan = store.getPan

        const scale = zoom * dpr
        const offsetX = pan.x * dpr
        const offsetY = pan.y * dpr
        ctx.setTransform( scale, 0, 0, scale, offsetX, offsetY )

        for ( const elementId of selectedElements ) {

            const el = elements.find( e => e.id === elementId )
            ctx.save()
            ctx.strokeStyle = "#3986ee"
            ctx.lineWidth = 1
            ctx.shadowBlur = 10
            ctx.shadowColor = "#3986ee"
            ctx.beginPath()

            ctx.setLineDash( el.lineDash )
            const board = getCanvasCSSSize( drawCanvasRef.current )

            if ( el.type === "stroke" ) {
                const firstPoint = denormalizePoint( el.points[0] )
                ctx.moveTo( firstPoint.x, firstPoint.y )
                for ( let i = 1; i < el.points.length; i++ ) {
                    const denormalizedPoint = denormalizePoint( el.points[i] )
                    ctx.lineTo( denormalizedPoint.x, denormalizedPoint.y )
                }
            } else if ( el.type === "rect" || el.type === "text" ) {
                const x = el.x * board.width
                const y = el.y * board.height
                const width = el.width * board.width
                const height = el.height * board.height
                ctx.strokeRect( x, y, width, height )
            } else if ( el.type === "circle" ) {
                const centerX = el.centerX * board.width
                const centerY = el.centerY * board.height
                const radiusX = el.radiusX * board.width
                const radiusY = el.radiusY * board.height
                ctx.ellipse( centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI )
            }

            ctx.stroke()
            ctx.restore()
        }

    }, [store] )

    const redrawAll = useCallback( ( leave = [] ) => {
        const bgCtx = bgCanvasCtxRef.current
        const drawCtx = drawCanvasCtxRef.current
        const tempCtx = tempCanvasCtxRef.current

        if ( !bgCtx || !drawCtx || !tempCtx ) return

        const dpr = window.devicePixelRatio || 1
        const zoom = store.getZoom
        const pan = store.getPan

        // Reset to identity
        bgCtx.setTransform( 1, 0, 0, 1, 0, 0 )
        drawCtx.setTransform( 1, 0, 0, 1, 0, 0 )
        tempCtx.setTransform( 1, 0, 0, 1, 0, 0 )

        // Clear in backing pixels
        bgCtx.clearRect( 0, 0, bgCtx.canvas.width, bgCtx.canvas.height )
        drawCtx.clearRect( 0, 0, drawCtx.canvas.width, drawCtx.canvas.height )
        tempCtx.clearRect( 0, 0, tempCtx.canvas.width, tempCtx.canvas.height )

        // Apply DPR * zoom, pan also in CSS→backing space
        const scale = zoom * dpr
        const offsetX = pan.x * dpr
        const offsetY = pan.y * dpr

        bgCtx.setTransform( scale, 0, 0, scale, offsetX, offsetY )
        drawCtx.setTransform( scale, 0, 0, scale, offsetX, offsetY )
        tempCtx.setTransform( scale, 0, 0, scale, offsetX, offsetY )

        // Draw background in CSS coordinates
        const { width: boardW, height: boardH } = getCanvasCSSSize( drawCanvasRef.current )
        bgCtx.drawImage( bgImgRef.current, 0, 0, boardW, boardH )

        store.getElements.forEach( ( el ) => drawElement( drawCtx, el ) )
    }, [drawElement, store] )

    const redrawElements = useCallback( ( drawCtxOverride = null, elements ) => {
        const ctx = drawCtxOverride || drawCanvasCtxRef.current
        if ( !ctx ) return

        const dpr = window.devicePixelRatio || 1
        const zoom = store.getZoom
        const pan = store.getPan

        ctx.setTransform( 1, 0, 0, 1, 0, 0 )
        ctx.clearRect( 0, 0, ctx.canvas.width, ctx.canvas.height )

        const scale = zoom * dpr
        const offsetX = pan.x * dpr
        const offsetY = pan.y * dpr
        ctx.setTransform( scale, 0, 0, scale, offsetX, offsetY )

        elements.forEach( ( el ) => {
            if ( store.getBoardState.textEditing && store.getBoardState.textEditing.id === el.id ) return
            drawElement( ctx, el )
        } )
    }, [drawElement, store] )

    const highlightHovered = useCallback( ( elementId, tempCtx ) => {
        const tempCanvas = tempCtx || tempCanvasCtxRef.current

        if ( !tempCanvas ) return


        const element = store.getElements.find( el => el.id === elementId )
        if ( element )
            drawHighlighter( tempCanvas, element )

    }, [drawHighlighter, store] )

    const setupCanvases = useCallback( async ( img ) => {
        bgImgRef.current = img

        const canvasWrapper = canvasWrapperRef.current
        const drawCanvas = drawCanvasRef.current
        const tempCanvas = tempCanvasRef.current
        const bgCanvas = bgCanvasRef.current

        // if ( !drawCanvas || !bgCanvas ) return

        const aspect = img.naturalWidth / img.naturalHeight

        const { width: containerWidth, height: containerHeight } = containerRef.current.getBoundingClientRect()


        let width = containerWidth
        let maxWidth = containerWidth * 0.9
        if ( width > maxWidth ) {
            width = maxWidth
            width = width * aspect
        }
        let height = width / aspect
        let maxHeight = ( containerHeight * 0.95 ) - 50

        if ( height > maxHeight ) {
            height = maxHeight
            width = height * aspect
        }

        store.setDimentions( { width, height } )

        canvasWrapper.style.width = `${width}px`
        canvasWrapper.style.height = `${height}px`
        if ( !bgCanvas || !drawCanvas || !tempCanvas ) {
            console.warn( "Canvas not ready yet" )
            return
        }

        const dpr = window.devicePixelRatio || 1;
        [bgCanvas, drawCanvas, tempCanvas].forEach( ( c ) => {
            c.width = width * dpr     // physical pixels
            c.height = height * dpr

            c.style.width = `${width}px`   // CSS pixel size
            c.style.height = `${height}px`

            c.style.position = "absolute"
            c.style.top = 0
            c.style.left = 0

            const ctx = c.getContext( "2d" )
            // ctx.scale( dpr, dpr )  // Scale *drawing operations* back to CSS pixels
        } )

        const tempCtx = await tempCanvas.getContext( "2d" )
        const drawCtx = await drawCanvas.getContext( "2d" )
        const bgCtx = await bgCanvas.getContext( "2d" )

        bgCtx.drawImage( img, 0, 0, width, height )
        store.setBackground( img, width, height )

        tempCanvasCtxRef.current = tempCtx
        drawCanvasCtxRef.current = drawCtx
        bgCanvasCtxRef.current = bgCtx
        redrawAll()
    }, [redrawAll, store] )

    const onDoubleClick = useCallback( ( e ) => {
        if ( store.getTool === "select" ) {

            if ( hoveredId ) {
                const element = store.getElements.find( el => el.id === hoveredId )
                if ( element.type === "text" ) {
                    setTextEditing( { ...element } )
                    store.setBoardState( { textEditing: element } )
                    store.setTool( "" )
                    redrawElements( drawCanvasCtxRef.current, store.getElements )
                }
            }
            setSelectedElements( new Set() )
            return
        }
    }, [hoveredId, store, setSelectedElements, redrawElements] )

    const onDown = useCallback( ( e ) => {
        if ( store.getTool === "pan" ) {
            panningRef.current = true
            store.setBoardState( { panning: true } )
            panStartRef.current.x = e.clientX
            panStartRef.current.y = e.clientY
            e.preventDefault()

            return
        }

        const positions = getPos( e, drawCanvasRef.current, store.getPan, store.getZoom )
        if ( store.getTool === 'select' ) {
            if ( store.getTool === "select" ) {

                if ( hoveredId ) {
                    const newSelected = new Set( [...store.getBoardState.selected] )
                    if ( !newSelected.has( hoveredId ) ) {
                        if ( e.shiftKey ) {
                            newSelected.add( hoveredId )
                        } else {
                            newSelected.clear()
                            newSelected.add( hoveredId )
                        }
                        setSelectedElements( newSelected )
                    }

                    movingOffsetRef.current = positions.normalizedPage
                    movingStartRef.current = { ...positions.normalizedPage }
                    movingElementsRef.current = true
                    movedAfterElements.current = structuredClone( store.getElements )
                } else {
                    setSelectedElements( new Set() )
                }
                return
            }
        }

        drawingRef.current = true
        const start = positions.normalizedPage

        const toolConfig = VIRTUALBOARD_TOOLS[store.getTool] || {}
        const { fontSize = 16, shadowBlur, fontFamily = "Roboto", shadow, color: strokeColor, lineDash: strokeLineDash, lineWidthScale, opacity: strokeOpacity, lineJoin, fill } = store.getBoardGlobalSettings
        const { lineWidth = 1, lineDash: toolLineDash, opacity: toolOpacity = strokeOpacity, composite, strokeType, color: toolColor } = toolConfig

        const color = toolColor ?? strokeColor

        const currentItemSettings = {}
        if ( store.getTool === 'text' ) {
            currentItemSettings.strokeType = strokeType
            currentItemSettings.fontSize = normalizeHeight( fontSize )
            currentItemSettings.fontFamily = fontFamily
            currentItemSettings.lineJoin = "round"
            currentItemSettings.lineDash = []
            currentItemSettings.lineWidth = 1
            currentItemSettings.text = ""
            currentItemSettings.fill = true
            currentItemSettings.colorInfo = color
            currentElementRef.current = { id: new Date().getTime(), type: "text", x: start.x, y: start.y, width: normalizeWidth( 100 ), height: normalizeHeight( fontSize + 2 ), tool: store.getTool, ...currentItemSettings }
            return
        } else {
            currentItemSettings.strokeType = strokeType
            currentItemSettings.lineWidth = lineWidth * lineWidthScale
            currentItemSettings.lineCap = "round"
            currentItemSettings.lineDash = ( toolLineDash ?? strokeLineDash ?? [] ).map( val => val * currentItemSettings.lineWidth )
            currentItemSettings.lineJoin = lineJoin
            currentItemSettings.opacity = toolOpacity * strokeOpacity
            currentItemSettings.composite = composite
            currentItemSettings.shadowBlur = shadowBlur
            currentItemSettings.colorInfo = color
            currentItemSettings.fill = fill
        }
        if ( shadow )
            currentItemSettings.shadowColor = color ? `rgba(${color.r},${color.g},${color.b},${toolOpacity * strokeOpacity})` : ""

        if ( ["pen", "pencil", "brush", "eraser", "marker"].includes( store.getTool ) ) {
            if ( store.getTool === "eraser" ) {
                currentItemSettings.lineWidth = 10
                const remainingElements = []
                const removedElements = []
                store.getElements.forEach( el => {
                    if ( !isPointInsideElement( start, el ) )
                        remainingElements.push( el )
                    else
                        removedElements.push( el.id )
                } )

                if ( remainingElements.length !== store.getElements.length ) {
                    removedElements.forEach( e => selectedForDeleting.current.add( e.id ) )
                    redrawElements( null, store.getElements )
                }
                currentElementRef.current = { id: new Date().getTime(), type: "stroke", points: [start], tool: store.getTool, ...currentItemSettings }
            } else {
                currentElementRef.current = { id: new Date().getTime(), type: "stroke", points: [start], tool: store.getTool, ...currentItemSettings }
            }

        } else if ( store.getTool === "rect" ) {
            currentElementRef.current = { id: new Date().getTime(), type: "rect", x: start.x, y: start.y, width: 0, height: 0, tool: store.getTool, ...currentItemSettings, baseWidth: drawCanvasRef.current.width, baseHeight: drawCanvasRef.current.height }
        } else if ( store.getTool === "circle" ) {
            currentElementRef.current = { id: new Date().getTime(), type: "circle", cx: start.x, cy: start.y, radius: 0, tool: store.getTool, ...currentItemSettings, baseWidth: drawCanvasRef.current.width, baseHeight: drawCanvasRef.current.height }
        }
    }, [store, hoveredId, redrawElements, setSelectedElements] )

    const onMove = useCallback( ( e ) => {
        let positions = getPos( e, drawCanvasRef.current, store.getPan, store.getZoom )
        store.setCursor( positions )
        if ( canvasCursorRef.current ) {
            const cursorOffset = getCustomCursorOffset( store.getTool )
            canvasCursorRef.current.style.top = `${positions.canvas.y + cursorOffset.y}px`
            canvasCursorRef.current.style.left = `${positions.canvas.x + cursorOffset.x}px`
        }
        const pos = positions.normalizedPage
        if ( onMouseMove && typeof onMouseMove === 'function' )
            onMouseMove( { ...pos, canvasPos: positions.normalizedCanvas, zoom: store.getZoom, pan: store.getPan }, store.getTool, Boolean( currentElementRef.current ) ? "pressed" : "freemove" )

        if ( store.getTool === "select" ) {
            if ( !movingElementsRef.current ) {
                const hoverId = findHoveredShape( pos, store.getElements )

                if ( hoveredId !== hoverId )
                    setHoveredId( hoverId )
            }

            if ( movingElementsRef.current ) {
                for ( const elementId of store.getBoardState.selected ) {
                    const element = movedAfterElements.current.find( el => el.id === elementId )

                    if ( element ) {

                        if ( element.type === 'stroke' ) {
                            const dx = pos.x - movingOffsetRef.current.x
                            const dy = pos.y - movingOffsetRef.current.y
                            element.points.forEach( p => { p.x += dx; p.y += dy } )
                        } else if ( element.type === 'rect' || element.type === 'text' ) {
                            const dx = pos.x - movingOffsetRef.current.x
                            const dy = pos.y - movingOffsetRef.current.y
                            element.x += dx
                            element.y += dy
                        } else if ( element.type === 'circle' ) {
                            const dx = pos.x - movingOffsetRef.current.x
                            const dy = pos.y - movingOffsetRef.current.y
                            element.centerX += dx
                            element.centerY += dy

                        }
                    }
                }
                movingOffsetRef.current.x = pos.x
                movingOffsetRef.current.y = pos.y
                redrawElements( null, movedAfterElements.current )
                drawSelected( store.getBoardState.selected, movedAfterElements.current )
            }

            return
        }

        if ( !drawingRef.current && !panningRef.current ) return

        if ( panningRef.current ) {
            const pan = store.getPan

            const dx = panStartRef.current.x - e.clientX
            const dy = panStartRef.current.y - e.clientY
            const newPanX = pan.x - dx
            const newPanY = pan.y - dy


            // Constrain pan so background sticks to edges
            const constrained = constrainPan(
                newPanX,
                newPanY,
                store.getZoom,
                store.getDimentions.canvas.width,
                store.getDimentions.canvas.height,
                store.getDimentions.canvas.width,
                store.getDimentions.canvas.height
            )

            store.setPan( constrained.x, constrained.y )

            panStartRef.current.x = e.clientX
            panStartRef.current.y = e.clientY

            redrawAll()
            addNewEventsToList( { type: 'zoom-and-pan', zoom: store.getZoom, pan: normalizePoint( constrained, drawCanvasRef.current.width, drawCanvasRef.current.height ) } )

        } else {
            if ( !currentElementRef.current ) return

            const ctx = tempCanvasCtxRef.current
            if ( currentElementRef.current.type === "stroke" ) {
                currentElementRef.current.points.push( pos )

                if ( store.getTool === "eraser" ) {

                    // filter elements not intersected by eraser
                    const remainingElements = []
                    const removedElements = []
                    store.getElements.forEach( el => {
                        if ( !isPointInsideElement( pos, el ) )
                            remainingElements.push( el )
                        else
                            removedElements.push( el.id )
                    } )

                    drawStroke( ctx, currentElementRef.current )
                    if ( remainingElements.length !== store.getElements.length ) {
                        removedElements.forEach( e => selectedForDeleting.current.add( e ) )
                        redrawElements( null, store.getElements )
                    }
                } else {

                    if ( !strictModeRef.current ) {
                        if ( strictModeUpdatedRef.current ) {
                            strictModeUpdatedRef.current = false
                        }
                        ctx.clearRect( 0, 0, ctx.canvas.width, ctx.canvas.height )
                        drawElement( ctx, currentElementRef.current )
                    } else {
                        ctx.clearRect( 0, 0, ctx.canvas.width, ctx.canvas.height )
                        drawLine( ctx, currentElementRef.current )
                    }
                }
            } else if ( currentElementRef.current.type === "rect" ) {
                const { x, y } = currentElementRef.current
                if ( strictModeRef.current ) {
                    const denormalizedStartPoints = denormalizePoint( { x, y } )
                    const currentDenormalizedPoints = positions.page
                    const size = Math.max( ( currentDenormalizedPoints.x - denormalizedStartPoints.x ), ( currentDenormalizedPoints.y - denormalizedStartPoints.y ) )
                    currentElementRef.current.width = normalizeWidth( size )
                    currentElementRef.current.height = normalizeHeight( size )
                } else {
                    currentElementRef.current.width = pos.x - x
                    currentElementRef.current.height = pos.y - y
                }
                redrawAll()
                drawElement( ctx, currentElementRef.current )
            } else if ( currentElementRef.current.type === "text" ) {
                const { x, y } = currentElementRef.current
                currentElementRef.current.width = pos.x - x
                currentElementRef.current.height = pos.y - y
                // redrawAll()
                previewTextElement( ctx, currentElementRef.current )
            } else if ( currentElementRef.current.type === "circle" ) {
                const dx = pos.x - currentElementRef.current.cx
                const dy = pos.y - currentElementRef.current.cy
                let radiusX = Math.abs( pos.x - currentElementRef.current.cx ) / 2
                let radiusY = Math.abs( pos.y - currentElementRef.current.cy ) / 2
                if ( !strictModeRef.current ) {
                    currentElementRef.current.radiusX = radiusX
                    currentElementRef.current.radiusY = radiusY
                } else {
                    const denormalizedRadiusX = denormalizeWidth( radiusX )
                    const denormalizedRadiusY = denormalizeHeight( radiusY )
                    const size = Math.max( denormalizedRadiusX, denormalizedRadiusY )
                    currentElementRef.current.radiusX = radiusX = normalizeWidth( size )
                    currentElementRef.current.radiusY = radiusY = normalizeHeight( size )
                }
                const centerX = Math.min( currentElementRef.current.cx, pos.x ) + radiusX
                const centerY = Math.min( currentElementRef.current.cy, pos.y ) + radiusY
                currentElementRef.current.radius = Math.sqrt( dx * dx + dy * dy )
                currentElementRef.current.centerX = centerX
                currentElementRef.current.centerY = centerY

                redrawAll()
                drawElement( ctx, currentElementRef.current )
            }
        }
    }, [hoveredId, drawElement, drawLine, drawSelected, drawStroke, onMouseMove, redrawAll, redrawElements, store, addNewEventsToList] )

    const onUp = useCallback( ( e ) => {
        if ( store.getTool === "select" && movingElementsRef.current ) {

            if ( movingOffsetRef.current.x !== movingStartRef.current.x || movingOffsetRef.current.y !== movingStartRef.current.y ) {
                const movedElements = movedAfterElements.current.filter( el => store.getBoardState.selected.has( el.id ) )
                addNewEventsToList( { type: 'elements-moved', elements: movedElements, versionId: new Date().getTime(), timestamp: new Date() } )
            }
        }
        if ( currentElementRef.current ) {

            const tempCtx = tempCanvasCtxRef.current
            if ( tempCtx )
                tempCtx.clearRect( 0, 0, tempCtx.canvas.width, tempCtx.canvas.height )
            if ( store.getTool !== "eraser" && currentElementRef.current.type !== "text" ) {
                if ( currentElementRef.current.type === "stroke" && strictModeRef.current ) {
                    const points = currentElementRef.current.points
                    currentElementRef.current.points = [points.at( 0 ), points.at( -1 )]
                }
                // store.addElement( currentElementRef.current )
                addNewEventsToList( { type: 'element-added', element: currentElementRef.current, versionId: new Date().getTime(), timestamp: new Date() } )
                if ( !store.getContainerState.lockToTool ) {
                    store.setTool( "select" )
                    store.setBoardState( { selected: new Set( [currentElementRef.current.id] ) } )
                } else if ( currentElementRef.current.type === "text" )
                    store.setTool( "select" )

            } else if ( store.getTool === "eraser" ) {
                store.getElements.filter( el => selectedForDeleting.current.has( el.id ) )
                const event = store.removeSelected( selectedForDeleting.current )
                if ( event?.elements?.length > 0 )
                    addNewEventsToList( event )
            } else if ( store.getTool === "text" ) {
                setNewTextElementAdder( currentElementRef.current )
                store.setTool( "" )
            }
        }
        if ( panningRef.current )
            store.setBoardState( { panning: false } )

        selectedForDeleting.current = new Set()
        currentElementRef.current = null
        drawingRef.current = false
        panningRef.current = false
        movingOffsetRef.current = { x: 0, y: 0 }
        movingStartRef.current = { x: 0, y: 0 }
        movingElementsRef.current = false
        movedAfterElements.current = []

    }, [store, addNewEventsToList] )

    const handleNewTextNodeAdd = ( event ) => {
        const { value } = event.target
        if ( Boolean( value?.trim() ) ) {

            addNewEventsToList( { type: 'element-added', element: { ...newTextElementAdder }, versionId: new Date().getTime(), timestamp: new Date() } )
        }
        store.setTool( "select" )
        setNewTextElementAdder( false )
    }

    const handleNewTextNodeUpdate = ( e ) => {
        const element = store.getElements.find( el => el.id === textEditing.id )
        if ( element.text !== e.target.value ) {
            const updateEvent = store.updateElements( [textEditing.id], { text: e.target.value, width: textEditing.width || element.width, height: textEditing.height || element.height } )
            addNewEventsToList( updateEvent )
        }
        store.setBoardState( { textEditing: null } )
        store.setTool( "select" )
        setTextEditing( null )
    }

    const zoomToPoint = useCallback( ( scaleFactor, x, y ) => {
        const zoom = store.getZoom
        const pan = store.getPan

        const newZoom = Math.min( Math.max( zoom * scaleFactor, 1 ), 4 )

        const dx = x - pan.x
        const dy = y - pan.y
        const newPanX = x - dx * ( newZoom / zoom )
        const newPanY = y - dy * ( newZoom / zoom )
        const constrained = constrainPan( newPanX, newPanY, newZoom, store.getDimentions.canvas.width, store.getDimentions.canvas.height, store.getDimentions.canvas.width, store.getDimentions.canvas.height )

        store.setZoom( newZoom )
        store.setPan( constrained.x, constrained.y )

        addNewEventsToList( { type: 'zoom-and-pan', zoom: newZoom, pan: normalizePoint( constrained, drawCanvasRef.current.width, drawCanvasRef.current.height ) } )
        redrawAll()
    }, [store, addNewEventsToList, redrawAll] )

    const onZoom = useCallback( ( e ) => {
        e.preventDefault()
        const zoomDelta = e.deltaY < 0 ? 1.1 : 0.9
        const rect = e.target.getBoundingClientRect()
        zoomToPoint( zoomDelta, e.clientX - rect.left, e.clientY - rect.top )
    }, [zoomToPoint] )

    const handleKeyDown = ( event ) => {
        const { code } = event
        console.log( code )

        if ( ["ControlLeft", "ControlRight"].includes( code ) ) {
            strictModeRef.current = true
            strictModeUpdatedRef.current = true
        } else if ( ["Delete", "Backspace", "NumpadDecimal"].includes( code ) ) {
            const event = store.removeSelected()
            if ( event.elements.length > 0 )
                addNewEventsToList( event )
        } else if ( ["KeyE"].includes( code ) ) {
            store.setTool( 'eraser' )
        } else if ( ["KeyV"].includes( code ) ) {
            store.setTool( 'select' )
        } else if ( ["KeyP"].includes( code ) ) {
            store.setTool( 'pen' )
        } else if ( ["KeyH"].includes( code ) ) {
            store.setTool( 'pan' )
        } else if ( ["KeyM"].includes( code ) ) {
            store.setTool( 'marker' )
        } else if ( ["KeyT"].includes( code ) ) {
            store.setTool( 'text' )
        } else if ( ["KeyB"].includes( code ) ) {
            store.setTool( 'rect' )
        } else if ( ["KeyC"].includes( code ) ) {
            store.setTool( 'circle' )
        }

    }
    const handleKeyUp = ( event ) => {
        const { code } = event
        if ( ["ControlLeft", "ControlRight"].includes( code ) ) {
            strictModeRef.current = false
            strictModeUpdatedRef.current = true
        }
    }

    // useEffect( () => {
    //     if ( document && document.slides && Array.isArray( document.slides ) ) {
    //         store.resetObservables()
    //         store.setBoardState( { document: document } )
    //         setCurrentImage( document.slides[0] )
    //     }

    //     return () => {
    //         store.resetObservables()
    //     }
    // }, [document, store] )

    useEffect( () => {
        const currentImageUrl = store.getBoardState.document.slides[store.getBoardState.currentSlide].url

        if ( currentImageUrl ) {
            const img = new Image()
            img.crossOrigin = "anonymous"
            img.src = currentImageUrl
            img.onload = () => setupCanvases( img )
        }
    }, [store.getBoardState.currentSlide, currentImage, store.getBoardState.document.slides, setupCanvases] )

    useEffect( () => {
        const canvas = drawCanvasRef.current
        if ( !canvas ) return
        canvas.addEventListener( "pointerdown", onDown )
        // canvas.addEventListener( "touchstart", onDown )
        canvas.addEventListener( "dblclick", onDoubleClick )
        canvas.addEventListener( "pointermove", onMove )
        // canvas.addEventListener( "touchmove", onMove )
        canvas.addEventListener( "pointerup", onUp )
        // canvas.addEventListener( "touchend", onUp )
        window.document.addEventListener( "pointerup", onUp )
        window.document.addEventListener( "keyup", handleKeyUp )
        canvas.addEventListener( "wheel", onZoom )


        return () => {
            canvas.removeEventListener( "pointerdown", onDown )
            // canvas.removeEventListener( "touchstart", onDown )
            canvas.removeEventListener( "dblclick", onDoubleClick )
            canvas.removeEventListener( "pointermove", onMove )
            // canvas.removeEventListener( "touchmove", onMove )
            canvas.removeEventListener( "pointerup", onUp )
            // canvas.removeEventListener( "touchend", onUp )
            window.document.removeEventListener( "pointerup", onUp )
            window.document.removeEventListener( "keyup", handleKeyUp )
            canvas.removeEventListener( "wheel", onZoom )
        }
    }, [onMove, onDown, onDoubleClick, onZoom, onUp] )


    useEffect( () => {
        if ( drawCanvasCtxRef.current ) {
            redrawAll()
        }
    }, [store.getElements, redrawAll] )

    useEffect( () => {
        try {
            const element = containerRef.current
            if ( !element ) return

            const observer = new ResizeObserver( ( entries ) => {
                requestAnimationFrame( () => {
                    if ( !bgImgRef.current ) return

                    const img = bgImgRef.current
                    const canvasWrapper = canvasWrapperRef.current
                    const tempCanvas = tempCanvasRef.current
                    const drawCanvas = drawCanvasRef.current
                    const bgCanvas = bgCanvasRef.current

                    if ( !bgCanvas || !drawCanvas || !tempCanvas ) {
                        console.warn( "Canvas not ready yet" )
                        return
                    }

                    const aspect = img.naturalWidth / img.naturalHeight
                    const { width: containerWidth, height: containerHeight } =
                        containerRef.current.getBoundingClientRect()

                    // --- compute new board size in CSS px ---
                    let width = containerWidth * 0.9
                    let height = width / aspect
                    const maxHeight = containerHeight - 50

                    if ( height > maxHeight ) {
                        height = maxHeight
                        width = height * aspect
                    }

                    // store dimensions in CSS space
                    store.setDimentions( { width, height } )

                    // update wrapper size in CSS space
                    canvasWrapper.style.width = `${width}px`
                    canvasWrapper.style.height = `${height}px`

                    // --- DPR-aware canvas backing size ---
                    const dpr = window.devicePixelRatio || 1

                        ;[bgCanvas, drawCanvas, tempCanvas].forEach( ( c ) => {
                            // backing store
                            c.width = width * dpr
                            c.height = height * dpr

                            // CSS size
                            c.style.width = `${width}px`
                            c.style.height = `${height}px`

                            c.style.position = "absolute"
                            c.style.top = 0
                            c.style.left = 0
                        } )

                    // You don't really need to re-fetch contexts, but if you want:
                    const drawCtx = drawCanvas.getContext( "2d" )
                    const tempCtx = tempCanvas.getContext( "2d" )
                    const bgCtx = bgCanvas.getContext( "2d" )

                    tempCanvasCtxRef.current = tempCtx
                    drawCanvasCtxRef.current = drawCtx
                    bgCanvasCtxRef.current = bgCtx

                    // keep background meta in store (CSS size)
                    store.setBackground( img, width, height )

                    // let redrawAll handle DPR * zoom * pan + actual drawing
                    redrawAll()
                } )
            } )

            observer.observe( element )

            return () => observer.disconnect()
        } catch ( err ) {
            console.log( err )
        }
    }, [redrawAll, store] )

    useEffect( () => {

        if ( !newTextElementAdder?.id || !textEditing?.id ) return
        const element = textEditorRef.current
        if ( !element ) return

        const debouncedResizeHandler = debounce( "text-editor-resize", entries => {
            let { width, height } = entries[0].contentRect
            if ( textEditing.id ) {
                const updateEvent = store.updateElements( [+entries[0].target.id], { height: normalizeHeight( height ), width: normalizeWidth( width ) } )
                addNewEventsToList( updateEvent )
            }

        }, 1000 )

        const observer = new ResizeObserver( debouncedResizeHandler )
        console.log( "Editing Effect ran" )
        observer.observe( element )

        // Clean up the observer when the component unmounts
        return () => {
            observer.disconnect()
        }

    }, [textEditing?.id, newTextElementAdder?.id, addNewEventsToList, store] )

    useEffect( () => {
        const tempCanvas = tempCanvasCtxRef.current
        if ( !tempCanvas ) return
        tempCanvas.setTransform( store.getZoom, 0, 0, store.getZoom, store.getPan.x, store.getPan.y )
        tempCanvas.clearRect( 0, 0, tempCanvas.canvas.width, tempCanvas.canvas.height )

        drawSelected( store.getBoardState.selected, store.getElements )
        if ( hoveredId ) {
            highlightHovered( hoveredId )
        }


    }, [store.getBoardState.selected, hoveredId, store, drawSelected, highlightHovered] )

    useEffect( () => {
        const canvas = tempCanvasRef.current
        const ctx = canvas.getContext( "2d" )
        let animationFrameId

        const fadeLoop = () => {
            if ( store.getTool === "eraser" ) {
                ctx.globalCompositeOperation = 'destination-out'
                ctx.fillStyle = "rgba(0, 0, 0, 0.1)" // fade speed
                ctx.fillRect( 0, 0, canvas.clientWidth, canvas.clientHeight )
                ctx.globalCompositeOperation = 'source-over'
            }
            animationFrameId = requestAnimationFrame( fadeLoop )
        }

        fadeLoop()

        // 🧹 Cleanup on unmount or mode change
        return () => cancelAnimationFrame( animationFrameId )
    }, [store] )

    return (
        <Box overflow="auto" tabIndex={0} width="100%" onKeyDown={handleKeyDown} onKeyUp={handleKeyUp} display="flex" alignItems="center" flexDirection="column" height="100%" justifyContent="center" ref={containerRef} sx={{ position: "relative", overflow: "hidden" }}>
            <Box sx={{ cursor: store.getTool ? store.getTool === "pan" ? store.getBoardState.panning ? "grabbing" : "grab" : "default" : "default", ":hover .cursors": { display: "block" } }} ref={canvasWrapperRef} position="relative">
                <Box component="canvas" sx={{ touchAction: "none", borderRadius: "10px 10px 0 0" }} ref={bgCanvasRef}></Box>
                <Box component="canvas" sx={{ touchAction: "none", borderRadius: "10px 10px 0 0" }} ref={drawCanvasRef}></Box>
                <Box component="canvas" sx={{ pointerEvents: "none", touchAction: "none", borderRadius: "10px 10px 0 0" }} ref={tempCanvasRef}></Box>
                <Box className="cursors" sx={{ pointerEvents: " none", display: "none", touchAction: "none", borderRadius: "10px 10px 0 0" }} fontSize="14px" ref={canvasCursorRef} position="absolute" >
                    {store.getTool === "select" && Icons.default.Select}
                    {store.getTool === "pencil" && Icons.default.Pencil}
                    {store.getTool === "pen" && Icons.default.Pen}
                    {store.getTool === "eraser" && Icons.default.Eraser}
                    {store.getTool === "marker" && Icons.default.Highlighter}
                    {store.getTool === "text" && Icons.default.TextCursor}
                    {["rect", "circle"].includes( store.getTool ) && Icons.default.Plus}
                </Box>


                {textEditing && (
                    <Box
                        ref={textEditorRef}
                        component="textarea"
                        value={textEditing.text}
                        id={textEditing.id}
                        autoFocus
                        onFocus={e => e.target.select()}
                        sx={{
                            background: "white",
                            position: "absolute",
                            left: `${denormalizeWidth( textEditing.x )}px`,
                            top: `${denormalizeHeight( textEditing.y )}px`,
                            fontSize: `${denormalizeHeight( textEditing.fontSize )}px`,
                            minWidth: `${denormalizeWidth( textEditing.width )}px`,
                            minHeight: `${denormalizeHeight( textEditing.height )}px`,
                            fontFamily: textEditing.fontFamily,
                            color: "black",
                            overflow: "hidden",
                            outline: "none",
                            borderRadius: "5px",
                            border: "none",
                            resize: "none"
                        }}
                        onKeyDown={e => e.stopPropagation()}
                        onChange={( e ) => handleTextChangeOnNewTextEleEditor( e, setTextEditing )}
                        onBlur={handleNewTextNodeUpdate}
                    />
                )}

                {newTextElementAdder && (
                    <Box
                        ref={textEditorRef}
                        component="textarea"
                        value={newTextElementAdder.text}
                        id={newTextElementAdder.id}
                        autoFocus
                        onFocus={e => e.target.select()}
                        sx={{
                            background: "transparent",
                            position: "absolute",
                            left: `${denormalizeWidth( newTextElementAdder.x )}px`,
                            top: `${denormalizeHeight( newTextElementAdder.y )}px`,
                            minWidth: `${denormalizeWidth( newTextElementAdder.width )}px`,
                            minHeight: `${denormalizeHeight( newTextElementAdder.height )}px`,
                            width: "auto",
                            height: "auto",
                            fontSize: `${denormalizeHeight( newTextElementAdder.fontSize )}px`,
                            fontFamily: newTextElementAdder.fontFamily,
                            color: "black",
                            // resize: "auto",
                            overflow: "hidden",
                            outline: "none",
                            // boxShadow: shadows[2],
                            border: "none",
                            borderRadius: "5px",
                            resize: "none"
                        }}
                        onKeyDown={e => e.stopPropagation()}
                        onChange={( e ) => handleTextChangeOnNewTextEleEditor( e, setNewTextElementAdder )}
                        onBlur={handleNewTextNodeAdd}
                    />
                )}

            </Box>

            {store.getBoardState?.document?.slides?.length > 0 && <CanvasBottomNavbar onChange={onChange} />}
            {!store.getContainerState.hideTools && <VirtualboardTools onEvent={addNewEventsToList} />}
            <MenuBar downloadMergedCanvas={downloadMergedCanvas} toggleFullScreen={toggleFullScreen} onChange={onChange} />
        </Box >
    )
} )

const InteractiveVirtualboard = observer( ( props ) => {
    const store = useVirtualboardStore()
    const newElements = useMemo( () => {
        return props.elements
    }, [props.elements] )

    useEffect( () => {
        store.setElements( newElements )

    }, [newElements, store] )

    return <Virtualboard {...props} />

} )

export default InteractiveVirtualboard